// base
import React, { createContext, useContext, useState, useCallback, startTransition } from 'react';

// components
import { AlertModalV2 } from 'components';

export interface AlertContextType {
  isShow: boolean;
  showAlert: (
    message: React.ReactNode,
    title?: string,
    onClose?: () => void,
    alertProps?: any
  ) => void;
  hideAlert: () => void;
}

const AlertContext = createContext<AlertContextType>({
  isShow: false,
  showAlert: () => {},
  hideAlert: () => {}
});

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState<React.ReactNode>('');
  const [closeCallback, setCloseCallback] = useState<(() => void) | undefined>();
  const [alertProps, setAlertProps] = useState({});

  const showAlert = useCallback(
    (message: React.ReactNode, title?: string, onClose?: () => void, alertProps = {}) => {
      startTransition(() => {
        setAlertTitle(title || 'Error');
        setAlertMessage(message);
        setCloseCallback(() => onClose);
        setIsOpen(true);
        setAlertProps(alertProps);
      });
    },
    []
  );

  const hideAlert = useCallback(() => {
    setIsOpen(false);
    setAlertTitle('');
    setAlertMessage('');

    if (closeCallback) {
      closeCallback();
      setCloseCallback(undefined);
    }
  }, [closeCallback]);

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert, isShow: isOpen }}>
      <AlertModalV2
        modalTitle={alertTitle}
        desc={alertMessage}
        onOk={hideAlert}
        onClose={hideAlert}
        open={isOpen}
        noMinWidth
        {...alertProps}
      />
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);

  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }

  return {
    ...context
  };
};
